<!--
  - 节假日按周几：某天按指定周几的营业时间和每周特殊价格走（cloud #437 的 /settings/dateAsWeekday）
  -->

<template>
  <div v-if="supported">
    <page-header>
      {{ $t('dateAsWeekdayTitle') }}
      <template #subtitle>
        {{ $t('dateAsWeekdaySub') }}
      </template>
      <template #action>
        <primary-button
          icon="mdi-plus"
          text="dateAsWeekdayAdd"
          @click="openNew"
        />
      </template>
    </page-header>
    <loading-provider
      :loading="loading"
      class="mt-4"
    >
      <div
        v-if="visibleList.length === 0"
        class="text-body-2 grey--text pa-2"
      >
        {{ $t('dateAsWeekdayEmpty') }}
      </div>
      <v-card
        v-else
        elevation="0"
        outlined
      >
        <div
          v-for="(r, i) in visibleList"
          :key="r.id"
          :class="['dw-row', i > 0 ? 'dw-row-border' : '', isPast(r) ? 'dw-row-past' : '']"
          @click="edit(r)"
        >
          <div class="dw-date">
            <span class="font-weight-bold">{{ r.date }}</span>
            <span class="grey--text ml-1">{{ weekdayName(isoWeekday(r.date)) }}</span>
          </div>
          <div class="dw-as">
            <v-icon
              class="mr-1"
              small
            >
              mdi-arrow-right
            </v-icon>
            {{ $t('dateAsWeekdayAsLabel', { day: weekdayName(r.asDayOfWeek) }) }}
          </div>
          <div class="dw-note grey--text text--darken-1">
            {{ r.note }}
          </div>
          <v-chip
            v-if="isPast(r)"
            class="mr-1"
            x-small
          >
            {{ $t('dateAsWeekdayPast') }}
          </v-chip>
          <v-btn
            icon
            small
            @click.stop="remove(r)"
          >
            <v-icon small>
              mdi-delete-outline
            </v-icon>
          </v-btn>
        </div>
      </v-card>
      <v-btn
        v-if="pastCount > 0"
        class="mt-1"
        small
        text
        @click="showPast = !showPast"
      >
        {{ showPast ? $t('dateAsWeekdayHidePast') : $t('dateAsWeekdayShowPast', { n: pastCount }) }}
      </v-btn>
    </loading-provider>
    <v-dialog
      v-model="showDialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="460"
    >
      <v-card v-if="form">
        <!-- 手机上全屏弹窗会把 v-card 的 padding 清零，所以内边距放在里面这一层 -->
        <div style="padding: 16px">
          <div class="d-flex align-center mb-4">
            <div
              class="font-weight-bold"
              style="font-size: 20px"
            >
              {{ form.id ? $t('dateAsWeekdayEdit') : $t('dateAsWeekdayAdd') }}
            </div>
            <v-spacer />
            <v-btn
              icon
              @click="showDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-form ref="form">
            <v-text-field
              v-model="form.date"
              :hint="form.date ? weekdayName(isoWeekday(form.date)) : ''"
              :label="$t('dateAsWeekdayDate')"
              :rules="[v => !!v || $t('cantBeEmpty')]"
              dense
              outlined
              persistent-hint
              type="date"
            />
            <v-select
              v-model="form.asDayOfWeek"
              :items="weekdayItems"
              :label="$t('dateAsWeekdayAs')"
              :rules="[v => !!v || $t('cantBeEmpty')]"
              class="mt-2"
              dense
              outlined
            />
            <v-text-field
              v-model="form.note"
              :label="$t('dateAsWeekdayNote')"
              dense
              outlined
            />
          </v-form>
          <div class="d-flex">
            <v-spacer />
            <v-btn
              class="mr-2"
              elevation="0"
              @click="showDialog = false"
            >
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              :loading="dialogLoading"
              color="amber lighten-4 black--text"
              elevation="0"
              @click="save"
            >
              <v-icon left>
                mdi-content-save
              </v-icon>
              {{ $t('save') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import PrimaryButton from '@/views/AadenPages/components/PrimaryButton.vue'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import WeekDays from '@/model/dataLayer/api/common/WeekDays'
import { sureToDelete } from '@/model/utils/utils'
import {
  deleteDateAsWeekday,
  getDateAsWeekdayList,
  saveDateAsWeekday
} from '@/model/dataLayer/service/reservation/dateAsWeekday'
import { isoWeekday } from '@/views/AadenPages/settings/reserveSetting/businessTime'
import dayjs from 'dayjs'

export default {
  name: 'DateAsWeekday',
  components: { LoadingProvider, PrimaryButton, PageHeader },
  data () {
    return {
      // 后端没有这个接口时整块不显示
      supported: false,
      loading: false,
      list: [],
      showPast: false,
      showDialog: false,
      dialogLoading: false,
      form: null
    }
  },
  computed: {
    today () {
      return dayjs().format('YYYY-MM-DD')
    },
    weekdayItems () {
      return WeekDays.getList().map(it => ({ text: it.name, value: it.id }))
    },
    sortedList () {
      return this.list.slice().sort((a, b) => String(a.date).localeCompare(String(b.date)))
    },
    pastCount () {
      return this.list.filter(r => this.isPast(r)).length
    },
    visibleList () {
      return this.showPast ? this.sortedList : this.sortedList.filter(r => !this.isPast(r))
    }
  },
  mounted () {
    this.reload()
  },
  methods: {
    isoWeekday,
    weekdayName (n) {
      return WeekDays.getList().find(it => it.id === Number(n))?.name ?? ''
    },
    isPast (r) {
      return String(r.date) < this.today
    },
    tip (message) {
      this.$store.commit('showTipsDialog', { tipsMessage: message })
    },
    openNew () {
      this.form = { id: null, date: null, asDayOfWeek: null, note: '' }
      this.showDialog = true
      this.$nextTick(() => this.$refs.form?.resetValidation())
    },
    edit (r) {
      this.form = { ...r }
      this.showDialog = true
    },
    async remove (r) {
      await sureToDelete(async () => {
        await deleteDateAsWeekday(r.id)
        await this.reload()
      })
    },
    async save () {
      if (this.$refs.form?.validate() === false) return
      const f = this.form
      if (!f.date || !f.asDayOfWeek) return
      // 云端同一天只有一条，存一个已有的日期会悄悄覆盖那一条，这里先拦下
      if (this.list.some(r => r.date === f.date && r.id !== f.id)) {
        this.tip(this.$t('dateAsWeekdayDuplicate'))
        return
      }
      this.dialogLoading = true
      try {
        const res = await saveDateAsWeekday({
          id: f.id || null,
          date: f.date,
          asDayOfWeek: Number(f.asDayOfWeek),
          note: f.note || null
        })
        if (res && res.code !== undefined && res.code !== 200) {
          this.tip(res.message || 'Error')
          return
        }
        this.showDialog = false
        this.form = null
      } finally {
        this.dialogLoading = false
        await this.reload()
      }
    },
    async reload () {
      this.loading = true
      try {
        const list = await getDateAsWeekdayList()
        this.supported = list !== null
        this.list = list ?? []
      } catch (e) {
        this.supported = false
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.dw-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  padding: 8px 16px;
  cursor: pointer;
}

.dw-row:hover {
  background: rgba(0, 0, 0, 0.03);
}

.dw-row-border {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.dw-row-past {
  opacity: 0.6;
}

.dw-date {
  min-width: 150px;
  font-variant-numeric: tabular-nums;
}

.dw-as {
  min-width: 140px;
}

.dw-note {
  flex: 1;
  min-width: 120px;
}
</style>
